import { Cross2Icon } from '@radix-ui/react-icons';
import { Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import { BlurredContainer } from '@/features/insights/components/blurred-container';
import { useCurrentOrgId } from '@/features/org/hooks/use-current-org-id';
import { ProjectDetails } from '@/features/projects/shared/api/get-project-details';
import { AddonType } from '@/features/subscriptions/data/addons-plans';
import { projectPlans } from '@/features/subscriptions/data/project-plans';
import { useAddonSubscriptions } from '@/features/subscriptions/hooks/use-addon-subscriptions';
import { useAssignAddon } from '@/features/subscriptions/hooks/use-assign-addon';
import { useAssignProjectPlan } from '@/features/subscriptions/hooks/use-assign-project-plan';
import { useAvailableProjectSubscriptions } from '@/features/subscriptions/hooks/use-available-project-subscriptions';
import { useCanManageSubscriptions } from '@/features/subscriptions/hooks/use-can-manage-subscriptions';
import { useCreateCheckoutSession } from '@/features/subscriptions/hooks/use-create-checkout-session';
import { usePaywall } from '@/features/subscriptions/hooks/use-paywall';
import { usePricing } from '@/features/subscriptions/hooks/use-pricing';
import { useProjectEntitlements } from '@/features/subscriptions/hooks/use-project-entitlements';
import { PlanType } from '@/features/subscriptions/types/subscription-types';
import { getCurrencySymbol } from '@/features/subscriptions/utils/format-price';
import {
  getIconBgColor,
  getNextUpgradePlan,
  getPlanIcon,
} from '@/features/subscriptions/utils/plan-utils';
import LineChartIcon from '@/shared/assets/line_chart.svg?react';
import PeopleIcon from '@/shared/assets/people.png';
import SubprojectIcon from '@/shared/assets/subproject.png';
import WarningTriangleIcon from '@/shared/assets/warning-triangle.png';
import { Button } from '@/shared/components/ui/button';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover';
import { useToast } from '@/shared/hooks/use-toast';
import {
  activeSubscriptionStatuses,
  ProjectSubscriptionWithEntitlementsAndUsage,
} from '@/shared/types/api';
import { cn } from '@/shared/utils/cn';
import { useErrorTranslation } from '@/shared/utils/error-handling';

export const ProjectPlansPopover = ({
  project,
}: {
  project: ProjectDetails;
}) => {
  const { t } = useTranslation('subscriptions');
  const {
    data: subscriptionWithEntitlements,
    isLoading: isLoadingSubscription,
  } = useProjectEntitlements(project.id);
  const { subscription, entitlements } = subscriptionWithEntitlements ?? {};
  const hasActiveSubscription =
    subscription && activeSubscriptionStatuses.includes(subscription.status);
  const displayPlanType = (entitlements?.plan_type ??
    subscription?.plan_type) as PlanType;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          className="min-w-[100px]"
          variant="secondary"
          btnType="secondary"
        >
          {isLoadingSubscription ? (
            <Loader2 className="size-4 animate-spin" />
          ) : hasActiveSubscription ? (
            <>
              <PlanIconAndName
                className="font-medium text-black"
                planType={displayPlanType}
              />
            </>
          ) : (
            <>
              <img
                src={WarningTriangleIcon}
                alt="warning"
                className="size-4 object-contain"
              />
              {t('projectPlans.noPlanSelected')}
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[400px] border border-white/10 bg-[#2a2a3e] p-0"
        align="center"
      >
        <div className="flex flex-col px-4 py-2">
          <div className="flex items-center text-2xs text-white">
            <span className="flex-1">{t('projectPlans.badge')}</span>
            <PopoverClose asChild>
              <button className="cursor-pointer">
                <Cross2Icon className="size-4" />
              </button>
            </PopoverClose>
          </div>

          <div className="mb-4 mt-2 h-px w-full bg-custom-light-grey/10" />
          {subscriptionWithEntitlements && hasActiveSubscription ? (
            <HasActiveSubscriptionBody
              project={project}
              subscriptionWithEntitlements={subscriptionWithEntitlements ?? {}}
            />
          ) : (
            <NoPlanBody project={project} />
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const PlanIconWithBg = ({ planType }: { planType: PlanType }) => {
  return (
    <div
      className={cn(
        'flex size-5 items-center justify-center rounded-full p-1',
        getIconBgColor('project'),
      )}
    >
      <img
        src={getPlanIcon(planType) ?? undefined}
        className="size-3 object-contain"
      />
    </div>
  );
};

const PlanIconAndName = ({
  planType,
  className,
}: {
  planType: PlanType;
  className?: string;
}) => {
  const { t } = useTranslation('subscriptions');
  return (
    <div
      className={cn(
        'flex items-center gap-3 font-semibold text-white',
        className,
      )}
    >
      <PlanIconWithBg planType={planType} />
      <span>{t(`plans.${planType}.name` as any)}</span>
    </div>
  );
};

const HasActiveSubscriptionBody = ({
  project,
  subscriptionWithEntitlements,
}: {
  project: ProjectDetails;
  subscriptionWithEntitlements: ProjectSubscriptionWithEntitlementsAndUsage;
}) => {
  const { t: tProject } = useTranslation('project');
  const { subscription, entitlements } = subscriptionWithEntitlements ?? {};
  const navigate = useNavigate();
  const canManageSubscriptions = useCanManageSubscriptions();
  const displayPlanType = (entitlements?.plan_type ??
    subscription?.plan_type) as PlanType;

  return (
    <div className="flex flex-col">
      <PlanIconAndName className="mb-4" planType={displayPlanType} />
      <BlurredContainer isBlurred={!entitlements}>
        <div className="mb-6 flex flex-col gap-6 rounded-xs bg-primary-shade-grey-500/5 p-4">
          <span className="mb-0 text-xs font-semibold text-white">
            {tProject('ProjectPlansDialog.contingent')}
          </span>
          {(
            [
              'extra_seat',
              'extra_subproject',
              'extra_derivative_kpi',
            ] as AddonType[]
          )
            .filter(
              (addonType) =>
                !(
                  addonType === 'extra_subproject' && project.type !== 'project'
                ),
            )
            .map((addonType) => (
              <EntitlementUsageEntry
                projectId={project.id}
                addonType={addonType}
                subscriptionWithEntitlements={subscriptionWithEntitlements}
              />
            ))}
        </div>
      </BlurredContainer>
      {canManageSubscriptions && (
        <div className="flex flex-col gap-3">
          {getNextUpgradePlan(displayPlanType) && (
            <Button
              className="w-full"
              variant="greenGradient"
              btnType="secondary"
              onClick={() => {
                navigate(paths.plansManagement.getHref('project'));
              }}
            >
              {tProject('ProjectPlansDialog.upgrade_now')}
            </Button>
          )}
          <Button
            className="w-full text-white"
            variant="outline"
            btnType="secondary"
            onClick={() => {
              navigate(paths.plansManagement.getHref('project'));
            }}
          >
            {tProject('ProjectPlansDialog.manage_addons')}
          </Button>
        </div>
      )}
    </div>
  );
};

const NoPlanBody = ({ project }: { project: ProjectDetails }) => {
  const { t } = useTranslation('subscriptions');
  const { data: projectSubscriptions } = useAvailableProjectSubscriptions();
  const assignPlan = useAssignProjectPlan();
  const { t: tProject } = useTranslation('project');
  const { data: pricing } = usePricing();
  const navigate = useNavigate();
  const createCheckout = useCreateCheckoutSession();
  const organizationId = useCurrentOrgId();
  const { toast } = useToast();
  const errorTranslation = useErrorTranslation();
  const { openPaywallFromError } = usePaywall();
  const canManageSubscriptions = useCanManageSubscriptions();
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
  const [assigningSubscriptionId, setAssigningSubscriptionId] = useState<
    string | null
  >(null);

  const remainingPlans = projectPlans.filter(
    (plan) =>
      !projectSubscriptions?.some(
        (subscription) => subscription.plan_type === plan.planType,
      ),
  );

  const handleAssignPlan = (subscriptionId: string) => {
    setAssigningSubscriptionId(subscriptionId);
    assignPlan.mutate(
      {
        projectId: project.id,
        subscriptionId,
      },
      {
        onSettled: () => {
          setAssigningSubscriptionId(null);
        },
      },
    );
  };

  if (!canManageSubscriptions) {
    return (
      <>
        <div className="mb-4 flex items-center justify-center gap-1 rounded-full bg-[hsl(36_79%_52%_/0.3)] py-2">
          <img
            src={WarningTriangleIcon}
            alt="warning"
            className="size-5 object-contain"
          />
          <p className="text-3xs font-medium text-white">
            {t('projectPlans.noActivePlan')}
          </p>
        </div>
        <p className="mb-4 text-center text-sm text-white/60">
          {t('paywall.memberRestriction.description')}
        </p>
      </>
    );
  }

  return (
    <>
      <div className="mb-4 flex items-center justify-center gap-1 rounded-full bg-[hsl(36_79%_52%_/0.3)] py-2">
        <img
          src={WarningTriangleIcon}
          alt="warning"
          className="size-5 object-contain"
        />
        <p className="text-3xs font-medium text-white">
          {t('projectPlans.noActivePlan')}
        </p>
      </div>

      {projectSubscriptions && projectSubscriptions.length > 0 ? (
        <div className="flex flex-col gap-3">
          <p className="text-xs font-semibold text-white">
            {tProject('ProjectPlansDialog.contingent')}
          </p>
          {projectSubscriptions.map((subscription) => {
            const isAssigning = assigningSubscriptionId === subscription.id;
            const isCurrentProjectAssigned =
              project.subscription_id === subscription.id;

            return (
              <div
                className="flex flex-col gap-3 rounded-xs bg-primary-shade-grey-500/5 p-4"
                key={subscription.id}
              >
                <div className="flex flex-row items-center justify-between gap-1.5">
                  <PlanIconAndName
                    planType={subscription.plan_type as PlanType}
                  />
                  {isCurrentProjectAssigned ? (
                    <Button
                      className="min-w-[140px]"
                      variant="greenGradient"
                      btnType="secondary"
                      disabled
                    >
                      {tProject('ProjectPlansDialog.assigned')}
                    </Button>
                  ) : (
                    <Button
                      className="min-w-[140px]"
                      variant="greenGradient"
                      btnType="secondary"
                      disabled={isAssigning}
                      onClick={() => handleAssignPlan(subscription.id)}
                    >
                      {isAssigning ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        tProject('ProjectPlansDialog.appoint_plan')
                      )}
                    </Button>
                  )}
                </div>
                {isAssigning && (
                  <p className="text-3xs text-white/60">
                    {t('projectPlans.assigning')}
                  </p>
                )}
              </div>
            );
          })}
          {remainingPlans.length > 0 && (
            <p className="mt-6 text-xs font-semibold text-white">
              {tProject('ProjectPlansDialog.or_buy_plan')}
            </p>
          )}
        </div>
      ) : null}

      {remainingPlans.length > 0 && (
        <div className="mb-6 mt-4 flex flex-col gap-4">
          {remainingPlans.map((plan) => {
            const monthlyPlan = pricing?.[plan.planType]?.monthly;

            return (
              <div
                key={plan.id}
                className="flex flex-col items-start justify-between rounded-xs bg-primary-shade-grey-500/5 p-4 transition-colors hover:bg-primary-shade-grey-500/10"
              >
                <PlanIconAndName planType={plan.planType} />
                <div className="flex-1">
                  <div className="flex items-center gap-2"></div>
                  <p className="mt-1 text-sm text-white/60">
                    {t(`plans.${plan.id}.description`)}
                  </p>
                  <p className="mt-1 text-xs text-white/40">
                    {t('projectPlans.billedYearly')}
                  </p>
                </div>
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-baseline gap-2">
                    <div className="text-3xl font-light text-white">
                      {monthlyPlan?.amount}
                      {getCurrencySymbol(monthlyPlan?.currency ?? 'eur')}
                    </div>
                    <div className="text-xs text-white/60">
                      {t('billing.perMonth')}
                    </div>
                  </div>
                  <Button
                    variant="greenGradient"
                    btnType={'secondary'}
                    className="min-w-[140px]"
                    disabled={loadingPlanId !== null}
                    onClick={() => {
                      setLoadingPlanId(plan.planType);
                      createCheckout.mutate(
                        {
                          organizationId: organizationId ?? 0,
                          planType: plan.planType,
                          billingPeriod: 'monthly',
                          projectId: project.id,
                        },
                        {
                          onSettled: () => {
                            setLoadingPlanId(null);
                          },
                          onError: async (error) => {
                            const handled = await openPaywallFromError(error);
                            if (!handled) {
                              toast({
                                title: errorTranslation(error),
                                variant: 'destructive',
                              });
                            }
                          },
                        },
                      );
                    }}
                  >
                    {loadingPlanId === plan.planType ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      t('projectPlans.selectPlan')
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Button
        variant={'ghost'}
        className="text-center text-sm text-white/60"
        onClick={() => {
          navigate(paths.plansManagement.path);
        }}
      >
        {t('projectPlans.viewAllPlans')}
      </Button>
    </>
  );
};

const EntitlementUsageEntry = ({
  projectId,
  addonType,
  subscriptionWithEntitlements,
}: {
  projectId: number;
  addonType: AddonType;
  subscriptionWithEntitlements: ProjectSubscriptionWithEntitlementsAndUsage;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('project');
  const { entitlements, usage } = subscriptionWithEntitlements ?? {};
  const { data: availableAddons, isLoading: loadingAddons } =
    useAddonSubscriptions();
  const { mutate, isPending: isAssigningAddon } = useAssignAddon();
  const canManageSubscriptions = useCanManageSubscriptions();

  const { text, buttonText, icon, current, total, remainingAddons } =
    useMemo(() => {
      switch (addonType) {
        case 'extra_seat':
          const seatsAvailable = availableAddons?.seats_available ?? 0;
          return {
            text: t('ProjectPlansDialog.seats'),
            icon: PeopleIcon,
            current: usage?.seats,
            total: entitlements?.seats,
            remainingAddons: seatsAvailable,
            buttonText:
              seatsAvailable > 0
                ? t('ProjectPlansDialog.add_seats', {
                    available: seatsAvailable,
                  })
                : t('ProjectPlansDialog.buy_seats'),
          };
        case 'extra_subproject':
          const subprojectsAvailable =
            availableAddons?.subprojects_available ?? 0;
          return {
            text: t('ProjectPlansDialog.subprojects'),
            icon: SubprojectIcon,
            current: usage?.subprojects,
            total: entitlements?.subprojects,
            remainingAddons: subprojectsAvailable,
            buttonText:
              subprojectsAvailable > 0
                ? t('ProjectPlansDialog.add_subprojects', {
                    available: subprojectsAvailable,
                  })
                : t('ProjectPlansDialog.buy_subprojects'),
          };
        case 'extra_derivative_kpi':
          const derivativeKpisAvailable =
            availableAddons?.derivative_kpis_available ?? 0;
          return {
            text: t('ProjectPlansDialog.derivative_kpis'),
            icon: <LineChartIcon className="size-4 text-white" />,
            current: usage?.derivative_kpis,
            total: entitlements?.derivative_kpis,
            remainingAddons: derivativeKpisAvailable,
            buttonText:
              derivativeKpisAvailable > 0
                ? t('ProjectPlansDialog.add_derivative_kpis', {
                    available: derivativeKpisAvailable,
                  })
                : t('ProjectPlansDialog.buy_derivative_kpis'),
          };
      }
    }, [addonType, usage, entitlements, t, availableAddons]);

  const percentage =
    current && total ? (total > 0 ? (current / total) * 100 : 0) : null;

  return (
    <div key={addonType} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-row items-center justify-between">
          <span className="flex flex-row items-center gap-1.5">
            {typeof icon === 'string' ? (
              <img src={icon} alt={text} className="size-4 object-contain" />
            ) : (
              icon
            )}
            <span className="text-2xs text-primary-shade-grey-500">{text}</span>
          </span>
          <span className="text-2xs text-white/60">
            {t('ProjectPlansDialog.usage_current_out_of_total', {
              current,
              total,
            })}
          </span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
          <div
            className="h-full bg-primary-shade-grey-500 transition-all"
            style={{ width: `${percentage ?? 0}%` }}
          />
        </div>
      </div>
      {canManageSubscriptions && (
        <Button
          variant="outline"
          btnType="secondary"
          className="w-full text-white"
          disabled={loadingAddons || isAssigningAddon}
          onClick={() => {
            if (remainingAddons > 0) {
              const addon = availableAddons?.addons?.find(
                (addon) => addon.addon_type === addonType,
              );
              if (addon) {
                mutate({
                  projectId,
                  addonId: addon.id,
                });
              }
            } else {
              navigate(paths.plansManagement.getHref('project'));
            }
          }}
        >
          {loadingAddons || isAssigningAddon ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            buttonText
          )}
        </Button>
      )}
    </div>
  );
};
